/**
 * Pure H OS · Wave A · Sidebar personalisation state.
 *
 * Tiny localStorage-backed hooks for:
 *   • Favorites  — user pins nav items to the top of the sidebar.
 *   • Recents    — the last 6 distinct nav-item routes visited by the user.
 *
 * Storage keys are namespaced with the user email so multiple users on the
 * same browser do not share state. Falls back to a shared bucket when the
 * user is not logged in.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { usePureH } from "../context/PureHContext";
import { NAV_ITEM_BY_PATH } from "../config/navigation";

const MAX_RECENTS = 6;

function favKey(email: string | null): string {
  return `pureh:sidebar:favorites:${email || "_anon"}:v1`;
}
function recKey(email: string | null): string {
  return `pureh:sidebar:recents:${email || "_anon"}:v1`;
}

function readList(k: string): string[] {
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === "string") : [];
  } catch { return []; }
}

function writeList(k: string, v: string[]): void {
  try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ }
}

/** Favorites: user-pinned nav-item keys. */
export function useFavorites() {
  const { user } = usePureH();
  const email = user?.email || null;
  const [favs, setFavs] = useState<string[]>(() => readList(favKey(email)));

  // Refresh on user change.
  useEffect(() => { setFavs(readList(favKey(email))); }, [email]);

  const toggle = useCallback((key: string) => {
    setFavs((cur) => {
      const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
      writeList(favKey(email), next);
      return next;
    });
  }, [email]);

  const isFav = useCallback((key: string) => favs.includes(key), [favs]);

  return { favs, toggle, isFav };
}

/** Recents: last N distinct routes visited, auto-tracked. */
export function useRecents() {
  const { user } = usePureH();
  const email = user?.email || null;
  const location = useLocation();
  const [recents, setRecents] = useState<string[]>(() => readList(recKey(email)));

  useEffect(() => { setRecents(readList(recKey(email))); }, [email]);

  useEffect(() => {
    const path = location.pathname;
    const match = NAV_ITEM_BY_PATH[path];
    if (!match) return;
    setRecents((cur) => {
      const filtered = cur.filter((k) => k !== match.key);
      const next = [match.key, ...filtered].slice(0, MAX_RECENTS);
      writeList(recKey(email), next);
      return next;
    });
  }, [location.pathname, email]);

  const clear = useCallback(() => {
    writeList(recKey(email), []);
    setRecents([]);
  }, [email]);

  return { recents, clear };
}

/** Combined convenience for the Sidebar renderer. */
export function useSidebarPersonalization() {
  const fav = useFavorites();
  const rec = useRecents();
  return useMemo(() => ({ ...fav, ...rec }), [fav, rec]);
}
